// src/contexts/UserContext.tsx
// Unified user context - single source of truth for current user identity

import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { auth, db } from '../lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { recordActivity } from '../lib/services/activityService';
import { getPrivateProfile, type PrivateProfileKind } from '../lib/privateProfile';
import { hasConsentForCurrentVersion } from '../lib/consent';

export interface CurrentUser {
  uid: string;
  email: string | null;
  displayName: string;
  photoURL: string;
  role: 'creator' | 'viewer';
  createdAt: string | null;
  youtubeLinked: boolean;
  channelId?: string;
  firstName?: string;
  lastName?: string;
  isAnonymous?: boolean;
  isPremium?: boolean;
  // T5 + T12-followup (LAUNCH_PLAN.md, 2026-05-15) — true whenever a parent
  // doc exists in creators/viewers/users (i.e., the user passed signup).
  // false only for the transient sub-second window between auth landing and
  // parent doc write. DOB is NOT required for `true` anymore: the previous
  // DOB-gated semantics trapped Google sign-ins (which never collect DOB).
  // Under-18 protection lives in GetStarted's email form (rejects <18 before
  // parent doc creation) + voluntary /complete-profile completion.
  hasOnboarded: boolean;
  // T6 (LAUNCH_PLAN.md): proof of consent to current ToS+Privacy version.
  // true when users/{uid}/consents/{CONSENT_VERSION} exists. false for any
  // user who hasn't accepted the current version — these users are blocked
  // by ConsentModal (rendered by ProtectedRoute) until they accept.
  hasConsent: boolean;
}

interface UserContextType {
  currentUser: CurrentUser | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const E2E_AUTH_BYPASS_ENABLED = import.meta.env.DEV && import.meta.env.VITE_E2E_AUTH_BYPASS === '1';
const E2E_AUTH_BYPASS_EMAIL =
  (import.meta.env.VITE_E2E_AUTH_EMAIL as string | undefined)?.trim() || 'e2e@viewsboost.local';
const E2E_AUTH_BYPASS_UID =
  (import.meta.env.VITE_E2E_AUTH_UID as string | undefined)?.trim() || 'studio-e2e-user';

function createE2eBypassFirebaseUser(): FirebaseUser {
  return {
    uid: E2E_AUTH_BYPASS_UID,
    email: E2E_AUTH_BYPASS_EMAIL,
    displayName: 'Studio E2E User',
    photoURL: '/images/avatar-placeholder.svg',
    emailVerified: true,
    isAnonymous: false,
    metadata: { creationTime: null, lastSignInTime: null },
    providerData: [],
    providerId: 'password',
    refreshToken: 'e2e-auth-bypass',
    tenantId: null,
    delete: async () => {},
    getIdToken: async () => 'e2e-auth-bypass-token',
    getIdTokenResult: async () =>
      ({
        token: 'e2e-auth-bypass-token',
        signInProvider: 'custom',
        signInSecondFactor: null,
        authTime: '',
        issuedAtTime: '',
        expirationTime: '',
        claims: {},
      }),
    reload: async () => {},
    toJSON: () => ({ uid: E2E_AUTH_BYPASS_UID, email: E2E_AUTH_BYPASS_EMAIL }),
  } as unknown as FirebaseUser;
}

function createE2eBypassCurrentUser(): CurrentUser {
  return {
    uid: E2E_AUTH_BYPASS_UID,
    email: E2E_AUTH_BYPASS_EMAIL,
    displayName: 'Studio E2E User',
    photoURL: '/images/avatar-placeholder.svg',
    role: 'creator',
    createdAt: null,
    youtubeLinked: false,
    // T5: E2E bypass user is treated as fully onboarded so test flows don't
    // hit the /complete-profile gate.
    hasOnboarded: true,
    // T6: same — bypass user has implicit consent so tests don't hit the modal.
    hasConsent: true,
  };
}

/**
 * Ensure users/{uid} doc exists. Creates it if missing.
 * This repairs legacy accounts that only have creators/viewers docs.
 */
async function ensureUserDoc(
  uid: string,
  email: string | null,
  role: 'creator' | 'viewer'
): Promise<void> {
  const userDocRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userDocRef);

  if (!userSnap.exists()) {
    await setDoc(userDocRef, {
      uid,
      email: email || '',
      role,
      createdAt: serverTimestamp(),
    });
    console.log(`[UserContext] Created missing users/${uid} doc`);
    // Record signup activity for new users
    recordActivity(uid, 'user.signup', 'New account created').catch(() => {});
  }
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(() =>
    E2E_AUTH_BYPASS_ENABLED ? createE2eBypassFirebaseUser() : null
  );
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(() =>
    E2E_AUTH_BYPASS_ENABLED ? createE2eBypassCurrentUser() : null
  );
  const [loading, setLoading] = useState(!E2E_AUTH_BYPASS_ENABLED);

  // Fetch profile data from Firestore
  const fetchProfile = async (user: FirebaseUser): Promise<CurrentUser | null> => {
    try {
      // Try creators collection first
      let snap = await getDoc(doc(db, 'creators', user.uid));
      let role: 'creator' | 'viewer' = 'creator';
      let kind: PrivateProfileKind = 'creators';

      if (!snap.exists()) {
        // Try viewers collection
        snap = await getDoc(doc(db, 'viewers', user.uid));
        role = 'viewer';
        kind = 'viewers';
      }

      if (!snap.exists()) {
        // Try generic users collection as fallback
        snap = await getDoc(doc(db, 'users', user.uid));
        role = 'viewer';
        kind = 'users';
      }

      if (snap.exists()) {
        const data = snap.data();
        // T1c (LAUNCH_PLAN.md): merge PII from <kind>/{uid}/private/profile when
        // available. Owner-readable by rule (T1a). Returns null if the private
        // doc doesn't exist yet (pre-T1d backfill) or on permission-denied —
        // either way, parent values are used as the fallback.
        const priv = await getPrivateProfile(user.uid, kind).catch(() => null);
        const firstName = priv?.firstName || data.firstName || '';
        const lastName  = priv?.lastName  || data.lastName  || '';
        // T1e: fallback chain when no real-name PII is available (post-T1f
        // for non-owner views, or partial-Google-signup paths). Order:
        //   explicit displayName  → first+last → channelName → username →
        //   role label. Email is intentionally not in this chain since email
        //   is PII per T1.
        const displayName = data.displayName ||
          (firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName) ||
          data.channelName ||
          data.username ||
          (role === 'creator' ? 'Creator' : 'Viewer');

        // T5 (LAUNCH_PLAN.md) + T12-followup (2026-05-15) — per user
        // directive: already-created users (parent doc exists in this branch)
        // are treated as onboarded regardless of DOB presence. The original
        // T5 redirect-to-/complete-profile was trapping users whose parent
        // doc was created via the Google path (handleGoogleSignIn writes
        // {uid, createdAt} only — no DOB) and any pre-T1 legacy user whose
        // backfill missed the DOB row.
        //
        // Under-18 protection IS preserved on the email signup path
        // (GetStarted form rejects <18 before parent doc creation, so users
        // with an email-created parent doc are guaranteed adult). It is
        // soft-weakened on the Google signup path — Google users without DOB
        // can enter the app. The CompleteProfile route stays mounted at
        // /complete-profile for voluntary completion.
        //
        // Pre-followup logic, kept for archeology:
        //   const hasOnboarded = Boolean(priv?.dob || data.dob);
        const hasOnboarded = true;

        // Repair: ensure users/{uid} doc exists (for legacy accounts)
        await ensureUserDoc(user.uid, user.email, role);

        // T6 (LAUNCH_PLAN.md): consent gate. Read users/{uid}/consents/<version>
        // — exists ⇒ true. Failures (offline, transient permission-denied) resolve
        // to false in the helper, which means ConsentModal will fire on next
        // protected-route navigation and offer a Try Again button.
        const hasConsent = await hasConsentForCurrentVersion(user.uid);

        return {
          uid: user.uid,
          email: user.email,
          displayName,
          photoURL: data.photoURL || user.photoURL || '/images/avatar-placeholder.svg',
          role,
          createdAt: data.createdAt || null,
          youtubeLinked: !!data.channelId,
          channelId: data.channelId,
          firstName,
          lastName,
          isPremium: data.isPremium === true,
          hasOnboarded,
          hasConsent,
        };
      }

      // No profile found - create users/{uid} doc and return minimal data from Firebase Auth
      // T5: brand-new sign-in (e.g., Google via /sign-in path that doesn't
      // create creators/viewers docs) → no DOB anywhere → not onboarded.
      // CompleteProfile page will create the proper kind doc + private/profile.
      await ensureUserDoc(user.uid, user.email, 'viewer');

      // T6: brand-new sign-in has no consent doc yet either.
      const hasConsent = await hasConsentForCurrentVersion(user.uid);

      return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || 'User',
        photoURL: user.photoURL || '/images/avatar-placeholder.svg',
        role: 'viewer',
        createdAt: null,
        youtubeLinked: false,
        hasOnboarded: false,
        hasConsent,
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Refresh profile manually (useful after profile updates)
  const refreshProfile = async () => {
    if (E2E_AUTH_BYPASS_ENABLED) {
      setCurrentUser(createE2eBypassCurrentUser());
      return;
    }
    if (firebaseUser) {
      const profile = await fetchProfile(firebaseUser);
      setCurrentUser(profile);
    }
  };

  // Track previous UID for logout activity recording
  const prevUidRef = useRef<string | null>(null);

  // Listen for auth state changes
  useEffect(() => {
    if (E2E_AUTH_BYPASS_ENABLED) {
      setFirebaseUser(createE2eBypassFirebaseUser());
      setCurrentUser(createE2eBypassCurrentUser());
      setLoading(false);
      prevUidRef.current = E2E_AUTH_BYPASS_UID;
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (user) {
        if (user.isAnonymous) {
          setCurrentUser(null);
          Sentry.setUser(null);
          prevUidRef.current = null;
          setLoading(false);
          return;
        }
        // Ensure Firestore SDK has a fresh auth token before making profile reads.
        // onAuthStateChanged can fire before the Firestore SDK syncs the new token,
        // (e.g. on page load with an expired token), causing permission-denied on the first reads.
        // Forcing a token refresh here ensures the Firestore SDK gets the updated token.
        try { await user.getIdToken(true); } catch { /* non-fatal */ }
        let profile = await fetchProfile(user);
        // If profile fetch fails (permission-denied during token sync), retry once with forced refresh.
        if (profile === null) {
          try {
            await user.getIdToken(true);
            profile = await fetchProfile(user);
          } catch { /* non-fatal */ }
        }
        setCurrentUser(profile);
        Sentry.setUser({ id: user.uid, username: user.displayName || undefined });
        // Record login activity (fire-and-forget)
        recordActivity(user.uid, 'auth.login', 'User signed in').catch(() => {});
        prevUidRef.current = user.uid;
      } else {
        // Record logout for previous user before clearing state
        if (prevUidRef.current) {
          recordActivity(prevUidRef.current, 'auth.logout', 'User signed out').catch(() => {});
          prevUidRef.current = null;
        }
        setCurrentUser(null);
        Sentry.setUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = useMemo(() => ({
    currentUser,
    firebaseUser,
    loading,
    refreshProfile,
  }), [currentUser, firebaseUser, loading]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useCurrentUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useCurrentUser must be used within a UserProvider');
  }
  return context;
}

// Helper: Format "Member since" from createdAt
export function formatMemberSince(createdAt: string | null): string {
  if (!createdAt) return 'Member';

  try {
    const date = new Date(createdAt);
    const year = date.getFullYear();
    const month = date.toLocaleString('default', { month: 'short' });

    // If same year, show "Member since Jan 2024"
    // If different year, just show "Member since 2023"
    const currentYear = new Date().getFullYear();
    if (year === currentYear) {
      return `Member since ${month} ${year}`;
    }
    return `Member since ${year}`;
  } catch {
    return 'Member';
  }
}
